import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import CTASection from "../components/CTASection";
import ArticleAd from "../ads/ArticleAd";
import { FAQBlock, LinkPills, SectionIntro, PillList, ResponsiveComparisonTable } from "../components/ContentBlocks";
import {
  robotSensorFamilies,
  sensorBuyingChecklist,
  sensorFailureModes,
  sensorFaq,
  sensorPriceRows,
  sensorSourceLinks
} from "../data/robotSensors";

const internalLinks = [
  { label: "Robot components", to: "/robot-components" },
  { label: "Robotics software", to: "/robotics-software" },
  { label: "Robotics companies", to: "/robotics-companies" },
  { label: "Arm joint components", to: "/robotic-arm-joint-components" },
  { label: "Robot management software", to: "/robot-management-software" },
  { label: "Robotics events", to: "/robotics-events" }
];

const mainTypes = [
  "RGB cameras",
  "stereo cameras",
  "depth cameras",
  "global shutter cameras",
  "event cameras",
  "thermal cameras",
  "2D LiDAR",
  "3D LiDAR",
  "safety LiDAR",
  "force torque sensors",
  "tactile sensors",
  "IMUs",
  "encoders",
  "proximity sensors",
  "safety light curtains"
];

function ExternalSource({ href, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-bold text-neutral-950 underline decoration-blue-300 decoration-2 underline-offset-4 hover:text-blue-700"
    >
      {children}
    </a>
  );
}

const sensorComparisonColumns = [
  { key: "name", label: "Sensor" },
  { key: "measures", label: "What it measures" },
  { key: "where", label: "Where it sits" },
  { key: "why", label: "Why it matters" },
  { key: "useCase", label: "Typical use case" },
  { key: "examples", label: "Example products" },
  { key: "limits", label: "Limits" },
  { key: "buyingChecks", label: "What to check" }
];

function SensorFamilyTable({ family }) {
  return (
    <ResponsiveComparisonTable
      columns={sensorComparisonColumns}
      rows={family.items}
      caption={`${family.group} sensor comparison`}
      ariaLabel={`${family.group} sensor comparison`}
      minWidth="min-w-[1240px]"
    />
  );
}

function PriceTable() {
  const columns = [
    { key: "type", label: "Sensor type" },
    { key: "product", label: "Example product" },
    { key: "use", label: "Typical use" },
    { key: "price", label: "Approximate public price" },
    { key: "currency", label: "Currency" },
    { key: "source", label: "Source" },
    { key: "lastChecked", label: "Last checked" },
    { key: "notes", label: "Notes" }
  ];
  const rows = sensorPriceRows.map((row) => ({
    ...row,
    source: <ExternalSource href={row.sourceUrl}>{row.source}</ExternalSource>
  }));

  return (
    <ResponsiveComparisonTable
      columns={columns}
      rows={rows}
      caption="Robot sensor price comparison"
      ariaLabel="Robot sensor price comparison"
      minWidth="min-w-[1280px]"
    />
  );
}

export default function RobotSensors() {
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "Robot Sensors", to: "/robot-sensors" }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: sensorFaq.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer
      }
    }))
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Robot Sensors Cameras, LiDAR, Force Sensors and Encoders",
    description: "A practical guide to robot sensors for vision, mapping, touch, balance, safety and control with product examples, public prices and buying checks.",
    author: { "@type": "Organization", name: "TechniaHQRobot" },
    publisher: { "@type": "Organization", name: "TechniaHQRobot" },
    mainEntityOfPage: "https://www.techniahqservice.com/robot-sensors"
  };

  return (
    <>
      <SEO
        title="Robot Sensors Cameras, LiDAR and Encoders"
        description="Robot sensors for vision, mapping, touch, balance, safety and control, with product examples, public prices, limits and buying checks."
        path="/robot-sensors"
        type="article"
        schema={[buildBreadcrumbSchema(breadcrumbs), articleSchema, faqSchema]}
      />
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader
        eyebrow="Robot hardware guide"
        title="Robot Sensors Cameras, LiDAR, Force Sensors and Encoders"
        description="Sensors give robots the ability to see, measure, touch, localize and react. The right choice depends on the robot body, environment, budget, safety requirement and software stack. A sensor that works on a lab bench can fail inside a dusty warehouse, a welding cell, a sunny sidewalk or a vibrating mobile base."
      />

      <section className="site-shell py-10 sm:py-14">
        <ArticleAd placement="top" articleSlug="robot-sensors" className="mb-8" />
        <div className="grid gap-5 lg:grid-cols-[1.4fr_0.9fr]">
          <div className="soft-card p-6 sm:p-8">
            <p className="eyebrow-chip">What are robot sensors?</p>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-neutral-950">Robot sensors turn physical motion into measurable data.</h2>
            <p className="mt-5 text-base leading-8 text-neutral-600">
              A robot sensor measures part of the real world or the robot body. Cameras measure visual scenes. LiDAR measures distance. Encoders measure joint or wheel position. Force torque sensors measure contact. IMUs measure motion. Safety scanners measure protected zones. The value comes from how the signal is mounted, calibrated, fused and acted on by software.
            </p>
            <p className="mt-4 text-base leading-8 text-neutral-600">
              For a real robot, sensor choice is a tradeoff between range, noise, speed, compute, certification, cost and failure recovery. A humanoid hand, an AMR and an industrial arm can all use cameras and force sensing, but the mounting, bandwidth and control loop are completely different.
            </p>
            <div className="mt-6">
              <LinkPills items={internalLinks} />
            </div>
          </div>
          <aside className="soft-card bg-neutral-950 p-6 text-white sm:p-8">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-blue-200">Main sensor types</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {mainTypes.map((type) => (
                <span key={type} className="rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold text-white/90">{type}</span>
              ))}
            </div>
            <p className="mt-6 text-sm leading-7 text-white/75">
              Manufacturer pages, official product pages and recognized distributors are used where public prices are visible. If a price is not public, the table says request quote instead of guessing.
            </p>
          </aside>
        </div>
      </section>

      <section className="site-shell py-12 sm:py-16">
        <SectionIntro
          eyebrow="Main types of robot sensors"
          title="A practical map of what each sensor measures"
          description="The table separates each sensor family by measurement type, mounting point, robot value, use case, product examples, limits and buying checks."
        />
        <div className="grid gap-10">
          {robotSensorFamilies.map((family) => (
            <section key={family.group} className="rounded-[2rem] border border-neutral-200 bg-white p-4 shadow-[0_20px_70px_rgba(15,23,42,0.06)] sm:p-6">
              <div className="grid gap-6 lg:grid-cols-[0.8fr_1.4fr] lg:items-center">
                <div className="overflow-hidden rounded-[1.5rem] border border-neutral-200 bg-neutral-50 p-6">
                  <img src={family.image} alt={family.imageAlt} className="h-48 w-full object-contain" loading="lazy" />
                </div>
                <div>
                  <p className="eyebrow-chip">{family.eyebrow}</p>
                  <h2 className="mt-4 text-3xl font-black tracking-tight text-neutral-950">{family.group}</h2>
                  <p className="mt-4 text-base leading-8 text-neutral-600">{family.summary}</p>
                </div>
              </div>
              <div className="mt-6">
                <SensorFamilyTable family={family} />
              </div>
            </section>
          ))}
        </div>
      </section>

      <section className="site-shell py-12 sm:py-16">
        <SectionIntro
          eyebrow="Robot sensor price examples"
          title="Public prices, quote only products and what the numbers mean"
          description="Prices change often and should be checked with the manufacturer or distributor before purchase. Distributor prices are not manufacturer MSRPs. Configuration, taxes, calibration, lenses, cables, brackets, safety controllers and software can change the real project cost."
        />
        <PriceTable />
      </section>

      <section className="site-shell py-12 sm:py-16">
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="soft-card p-6 sm:p-8">
            <p className="eyebrow-chip">How to choose a robot sensor</p>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-neutral-950">Start with the task, not the catalog.</h2>
            <ul className="mt-6 grid gap-3 text-sm leading-7 text-neutral-600">
              {sensorBuyingChecklist.map((item) => (
                <li key={item} className="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3">{item}</li>
              ))}
            </ul>
          </div>
          <div className="soft-card p-6 sm:p-8">
            <p className="eyebrow-chip">Limits and failure points</p>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-neutral-950">Every sensor has a failure mode.</h2>
            <div className="mt-6 grid gap-3">
              {sensorFailureModes.map((item) => (
                <article key={item.title} className="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3">
                  <h3 className="text-sm font-black text-neutral-950">{item.title}</h3>
                  <p className="mt-1 text-sm leading-7 text-neutral-600">{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="site-shell py-12 sm:py-16">
        <SectionIntro
          eyebrow="Internal reading path"
          title="Connect sensors to the rest of robot architecture"
          description="Sensors supply the measurements used by controllers, perception software and fleet systems. Continue with the hardware and integration guides below."
        />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {internalLinks.map((item) => (
            <Link key={item.to} to={item.to} className="soft-card soft-card-hover p-5">
              <p className="text-lg font-black text-neutral-950">{item.label}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="site-shell py-12 sm:py-16">
        <SectionIntro
          eyebrow="Sources"
          title="Manufacturer pages and distributor listings used"
          description="The source list keeps product pages, public prices and quote-only products separate. No manufacturer product images are hotlinked on this page."
        />
        <div className="soft-card p-6">
          <PillList items={["official product pages", "datasheets", "manufacturer stores", "recognized distributors", "public prices only"]} />
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {sensorSourceLinks.map((source) => (
              <ExternalSource key={source.url} href={source.url}>{source.label}</ExternalSource>
            ))}
          </div>
        </div>
      </section>

      <section className="site-shell pb-10 sm:pb-14">
        <ArticleAd placement="bottom" articleSlug="robot-sensors" />
      </section>

      <FAQBlock items={sensorFaq} />
      <CTASection title="Follow TechniaHQRobot for robot sensors, Physical AI and robotics hardware analysis" />
    </>
  );
}
